import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Loader } from '@progress/kendo-react-indicators';
import React from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks/store-hooks';
import { FPMSyncType } from '../../common/enums';
import useUserRights from '../../hooks/use-user-rights';
import { EditOutline, FileExportOutline, TrashOutline, UserGroupOutline, UserOutline } from '../../icons/icons';
import { exportDrawingObject } from './export';
import { DrawingObject, DrawingObjectCategory, DrawingObjectType } from './types';
import { deleteDrawingObject } from '../../app/state/slices/drawing-objects-slice';

interface DrawingObjectListProps {
    onEdit: (object: DrawingObject) => void;
}

const DrawingObjectList = (props: DrawingObjectListProps) => {
    const objects = useAppSelector((x) => x.drawingObjects.objects);
    const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
    const [actionObject, setActionObject] = React.useState<DrawingObject | null>(null);
    const [busy, setBusy] = React.useState(false);
    const rights = useUserRights();
    const dispatch = useAppDispatch();

    const askConfirmDeleteObject = React.useCallback((object: DrawingObject) => {
        setShowDeleteConfirm(true);
        setActionObject(object);
    }, []);

    const handleCancelDelete = React.useCallback(() => {
        setShowDeleteConfirm(false);
        setActionObject(null);
    }, []);

    const handleConfirmDelete = React.useCallback(async () => {
        setBusy(true);
        try {
            const result = await dispatch(deleteDrawingObject(actionObject!.id)).unwrap();
            if (!result.success) {
                return;
            }
            setShowDeleteConfirm(false);
            setActionObject(null);
        } finally {
            setBusy(false);
        }
    }, [actionObject]);

    const handleExportObject = React.useCallback(async (object: DrawingObject) => {
        await exportDrawingObject(object);
    }, []);

    return (
        <>
            {showDeleteConfirm && (
                <Dialog title={'Delete object'} closeIcon={false}>
                    <div>
                        <div>Do you want to delete this object:</div>
                        <strong>{actionObject?.name}</strong>
                    </div>
                    {busy && (
                        <div
                            style={{ position: 'absolute', background: 'rgba(0,0,0,0.20)', top: 0, bottom: 0, left: 0, right: 0 }}
                            className="d-flex align-items-center justify-content-center">
                            <Loader type={'infinite-spinner'} />
                        </div>
                    )}
                    <DialogActionsBar>
                        <div className="d-flex gap-2">
                            <Button
                                className="flex-grow-1 w-50"
                                themeColor={'error'}
                                fillMode={'outline'}
                                svgIcon={TrashOutline}
                                onClick={handleConfirmDelete}
                                disabled={busy}>
                                Delete
                            </Button>
                            <Button className="flex-grow-1 w-50" fillMode={'outline'} onClick={handleCancelDelete} disabled={busy}>
                                Cancel
                            </Button>
                        </div>
                    </DialogActionsBar>
                </Dialog>
            )}
            <div>
                <table className="cobalt-table cobalt-alternate w-100">
                    <thead>
                        <tr style={{ position: 'sticky', top: -10, height: 30, backgroundColor: 'var(--alternate-color)', zIndex: 2 }}>
                            <th className="ps-2">Name</th>
                            <th>Type</th>
                            <th>Label</th>
                            <th>Plane alert</th>
                            <th>Category</th>
                            <th>FPM</th>
                            <th className="text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {objects.map((x) => (
                            <tr key={x.id}>
                                <td className="ps-2">
                                    <SvgIcon icon={x.isCompany ? UserGroupOutline : UserOutline} />
                                    <span className="ps-1">{x.name}</span>
                                </td>
                                <td>{typeMapping[x.type]}</td>
                                <td>{x.label.text || '-'}</td>
                                <td>-</td>
                                <td>{categoryMapping[x.category]}</td>
                                <td>
                                    {x.fpmProperties?.sync ? (x.fpmProperties.FPMSyncType === FPMSyncType.global ? 'Global' : 'User') : '-'}
                                </td>
                                <td className="d-flex gap-2 justify-content-end">
                                    <Button
                                        svgIcon={FileExportOutline}
                                        fillMode={'outline'}
                                        className="button-small"
                                        title="Export"
                                        onClick={() => handleExportObject(x)}></Button>
                                    <Button
                                        disabled={x.isCompany && !rights.canEditCompanyViews}
                                        fillMode={'outline'}
                                        svgIcon={EditOutline}
                                        className="button-small"
                                        onClick={() => props.onEdit(x)}></Button>
                                    <Button
                                        disabled={x.isCompany && !rights.canEditCompanyViews}
                                        fillMode={'outline'}
                                        svgIcon={TrashOutline}
                                        className="button-small"
                                        onClick={() => askConfirmDeleteObject(x)}></Button>
                                </td>
                            </tr>
                        ))}
                        {objects.length === 0 && (
                            <tr>
                                <td colSpan={99} className="text-center">
                                    No items.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </>
    );
};

const typeMapping: Record<DrawingObjectType, string> = {
    [DrawingObjectType.circle]: 'Circle',
    [DrawingObjectType.rectangle]: 'Rectangle',
    [DrawingObjectType.polygon]: 'Polygon'
};

const categoryMapping: Record<DrawingObjectCategory, string> = {
    [DrawingObjectCategory.viewBased]: 'View based',
    [DrawingObjectCategory.alertBased]: 'Alert based',
    [DrawingObjectCategory.avoidance]: 'Avoidance area'
};
export default DrawingObjectList;
